import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { CreateCheckoutSession } from '@/modules/auth/use-cases'
import type { Currency, EmitFunnelEvent } from '@/shared/domain'
import { reportError } from '@/shared/infra/observability'
import type { BillingCycle, OrgFormData, OrgType } from '../types'

export const CREATE_ORG_MOBILE_DATA_STORAGE_KEY = 'createOrgMobileData'

export type UseCheckoutStepProps = {
  orgType: OrgType
  billingCycle: BillingCycle
  orgFormData: OrgFormData
  isMobile: boolean
  onCreateCheckoutSession: CreateCheckoutSession
  onEmitFunnelEvent: EmitFunnelEvent
  getAccessToken: () => Promise<string | null>
}

export type UseCheckoutStepResult = {
  isLoading: boolean
  error: string | null
  retry: () => void
}

function currencyForCountry(country: string): Currency {
  return country === 'CA' ? 'cad' : 'usd'
}

export function useCheckoutStep({
  orgType,
  billingCycle,
  orgFormData,
  isMobile,
  onCreateCheckoutSession,
  onEmitFunnelEvent,
  getAccessToken,
}: UseCheckoutStepProps): UseCheckoutStepResult {
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [attempt, setAttempt] = useState<number>(0)
  const startedRef = useRef<boolean>(false)

  const run = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const accessToken = (await getAccessToken()) ?? ''
      if (typeof window !== 'undefined') {
        window.sessionStorage.setItem(
          CREATE_ORG_MOBILE_DATA_STORAGE_KEY,
          JSON.stringify({
            orgType,
            orgName: orgFormData.orgName,
            orgState: orgFormData.state,
            orgCountry: orgFormData.country,
            billingCycle,
            promoCode: orgFormData.promoCode,
            accessToken,
            source: isMobile ? 'mobile' : 'web',
          }),
        )
      }

      const origin = typeof window !== 'undefined' ? window.location.origin : ''
      const result = await onCreateCheckoutSession({
        orgType,
        subscriptionTier: billingCycle,
        successUrl: `${origin}/register/complete?session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: `${origin}/register`,
        currency: currencyForCountry(orgFormData.country),
        orgName: orgFormData.orgName,
        orgState: orgFormData.state,
        ...(orgFormData.promotionCodeId ? { promotionCodeId: orgFormData.promotionCodeId } : {}),
      })

      if (!result.url) {
        setError('Failed to create checkout session')
        setIsLoading(false)
        return
      }

      onEmitFunnelEvent({ eventType: 'registration_step_completed', step: 'checkout' })

      if (typeof window !== 'undefined') {
        window.location.href = result.url
      }
    } catch (err) {
      setError(reportError(err, { fallbackMessage: 'Something went wrong. Please try again.' }))
      setIsLoading(false)
    }
  }, [
    orgType,
    billingCycle,
    orgFormData.orgName,
    orgFormData.state,
    orgFormData.country,
    orgFormData.promoCode,
    orgFormData.promotionCodeId,
    isMobile,
    onCreateCheckoutSession,
    onEmitFunnelEvent,
    getAccessToken,
  ])

  useEffect(() => {
    if (startedRef.current) return
    startedRef.current = true
    void run()
  }, [run, attempt])

  const retry = useCallback(() => {
    startedRef.current = false
    setAttempt((current) => current + 1)
  }, [])

  return useMemo(
    () => ({
      isLoading,
      error,
      retry,
    }),
    [isLoading, error, retry],
  )
}
